import {AbstractControl, ValidatorFn} from '@angular/forms';

/**
 * Single validator that checks autocomplete trigger syntax and returns the first applicable error:
 * - Nested: e.g. "{{ Variable {{ Variable }} }}" on the same line
 * - Unclosed/unopened: e.g. "{{ Variable }", "{{ Variable ", "{ Variable }}", "Variable }}"
 * - Empty: nothing (or only whitespace) between triggerStart and triggerEnd
 * - Invalid option: value between brackets is not in the allowed autocomplete options
 *
 * Each line is validated independently; triggers on different lines do not nest.
 */
export function textTriggerSyntaxValidator(
    triggerStart: string,
    triggerEnd: string,
    validOptionValues: Set<string>
): ValidatorFn {
    return (control: AbstractControl): { [key: string]: {isInvalid: boolean, triggerStart: string, triggerEnd: string} } | null => {
        const value = control.value;
        if (value == null || typeof value !== 'string') {
            return null;
        }
        if (!triggerStart || !triggerEnd) {
            return null;
        }

        for (const line of splitLines(value)) {
            const lineError = validateTriggerLine(line, triggerStart, triggerEnd, validOptionValues);
            if (lineError) {
                return lineError;
            }
        }
        return null;
    };
}

function validateTriggerLine(line: string, triggerStart: string, triggerEnd: string, validOptionValues: Set<string>): { [key: string]: {isInvalid: boolean, triggerStart: string, triggerEnd: string}} | null {
    const lenStart = triggerStart.length;
    const lenEnd = triggerEnd.length;
    let pos = 0;
    let depth = 0;
    const stack: number[] = [];

    while (pos < line.length) {
        const nextStart = line.indexOf(triggerStart, pos);
        const nextEnd = line.indexOf(triggerEnd, pos);
        const useStart = nextStart !== -1 && (nextEnd === -1 || nextStart < nextEnd);

        if (!useStart && nextEnd === -1) {
            break;
        }

        if (useStart) {
            if (depth > 0) {
                return {textTriggerNested: {isInvalid: true, triggerStart, triggerEnd}};
            }
            stack.push(nextStart);
            depth++;
            pos = nextStart + lenStart;
        } else {
            if (depth === 0) {
                return {textTriggerUnclosedOrUnopened: {isInvalid: true, triggerStart, triggerEnd}};
            }
            const startIdx = stack.pop()!;
            depth--;
            const content = line.substring(startIdx + lenStart, nextEnd);
            if (content.trim().length === 0) {
                return {textTriggerEmpty: {isInvalid: true, triggerStart, triggerEnd}};
            }
            if (validOptionValues.size > 0 && !validOptionValues.has(content)) {
                return {textTriggerInvalidOption: {isInvalid: true, triggerStart, triggerEnd}};
            }
            pos = nextEnd + lenEnd;
        }
    }

    if (depth > 0) {
        return {textTriggerUnclosedOrUnopened: {isInvalid: true, triggerStart, triggerEnd}};
    }
    return null;
}

function splitLines(value: string): string[] {
    return value.split(/\r\n|\n|\r/);
}
